const getEnvValue = <T extends string | number | boolean>(
  env: string | boolean | undefined,
  defaultValue: T,
): T => {
  try {
    if (env === undefined) return defaultValue;

    if (typeof defaultValue === typeof env) return env as T;

    if (!Number.isNaN(Number(env))) return Number(env) as T;

    return env as T;
  } catch {
    return defaultValue;
  }
};

export interface IServerConfig {
  zmyleApi: string;
  zmyleApiDefaultPath: string;
  prismicAccessToken: string;
  revalidateInSeconds: number;
  redisHost: string;
  redisPort: number;
  redisPassword: string;
  isProduction: boolean;
  ipBlacklist: string[];
  ipsumBlacklistUrl: string;
  ipWhitelist: string[];
  consoleNoWwwHostnames: boolean;
  isZmyleTestPaymentsEnabled: boolean;
  recaptchaSecretKey: string;
  idempotentTotalTimeoutSeconds: number;
  coreApi: {
    apiKey: string;
    apiSecret: string;
  };
  rateLimiter: {
    giftcard: { points: number; durationSeconds: number };
    checkout: { points: number; durationSeconds: number };
  };
  dev: {
    networkHostname: string;
  };
}

export class ServerConfig {
  // eslint-disable-next-line no-use-before-define
  private static instance: ServerConfig;

  private config: IServerConfig;

  private constructor() {
    this.config = {
      zmyleApi: getEnvValue(process.env.WEB_API, '').replace(/\/$/, ''),
      zmyleApiDefaultPath: '/ui_api/v1.1',
      prismicAccessToken: getEnvValue(process.env.PRISMIC_ACCESS_TOKEN, ''),
      revalidateInSeconds: 60 * 60, // One Hour, used for caching CMS content
      redisHost: getEnvValue(process.env.REDIS_HOST, ''),
      redisPort: getEnvValue(process.env.REDIS_PORT, 6379),
      redisPassword: getEnvValue(process.env.REDIS_PASSWORD, ''),
      recaptchaSecretKey: getEnvValue(process.env.RECAPTCHA_SECRET_KEY, ''),
      isProduction: process.env.NODE_ENV === 'production',
      ipsumBlacklistUrl: getEnvValue(
        process.env.IPSUM_BLACKLIST_URL,
        '',
      ).replace(/\/$/, ''),
      ipBlacklist: getEnvValue(process.env.IP_BLACKLIST, '')
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean),
      ipWhitelist: getEnvValue(process.env.IP_WHITELIST, '')
        .replace(/\s/g, '')
        .split(',')
        .filter(Boolean),
      isZmyleTestPaymentsEnabled: getEnvValue(
        process.env.ZMYLE_TEST_PAYMENT_ENABLED === 'true',
        false,
      ),
      consoleNoWwwHostnames: getEnvValue(
        process.env.CONSOLE_NO_WWW_HOSTNAMES === 'true',
        false,
      ),
      idempotentTotalTimeoutSeconds: getEnvValue(
        process.env.IDEMPOTENT_TOTAL_TIMEOUT_SECONDS,
        60,
      ),
      coreApi: {
        apiKey: getEnvValue(process.env.COREAPI_APIKEY, ''),
        apiSecret: getEnvValue(process.env.COREAPI_APISECRET, ''),
      },
      rateLimiter: {
        giftcard: {
          points: getEnvValue(
            process.env.RATELIMIT_GIFTCARD_SEARCH_FAILURES,
            64,
          ),
          durationSeconds: getEnvValue(
            process.env.RATELIMIT_GIFTCARD_SEARCH_DURATION_SECONDS,
            60 * 60, // 1 hour
          ),
        },
        checkout: {
          points:
            // Each checkout access consumes 2 points
            getEnvValue(process.env.RATELIMIT_CHECKOUT_SEARCH_FAILURES, 64) * 2,
          durationSeconds: getEnvValue(
            process.env.RATELIMIT_CHECKOUT_SEARCH_DURATION_SECONDS,
            60 * 60, // 1 hour
          ),
        },
      },
      dev: {
        networkHostname: getEnvValue(process.env.NETWORK_HOSTNAME, ''),
      },
    };
  }

  public static getInstance() {
    if (!ServerConfig.instance) ServerConfig.instance = new ServerConfig();

    return ServerConfig.instance;
  }

  public getConfig() {
    return this.config;
  }

  public setZmyleWebApi(zmyleWebApi: string) {
    this.config.zmyleApi = zmyleWebApi;
  }
}
